'use client'
import { useEffect, useRef } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';

const stats = [
  { number: 1281, label: 'clients' },
  { number: 230,  label: 'projects' },
  { number: 63,   label: 'collaborations' },
];

export default function AboutSection() {
  const t = useTranslations('About');

  const sectionRef    = useRef<HTMLElement>(null);
  const labelRef      = useRef<HTMLSpanElement>(null);
  const titleRef      = useRef<HTMLHeadingElement>(null);
  const subtitleRef   = useRef<HTMLSpanElement>(null);
  const bodyRef       = useRef<HTMLParagraphElement>(null);
  const linkRef       = useRef<HTMLDivElement>(null);
  const statsGridRef  = useRef<HTMLDivElement>(null);
  const numberRefs    = useRef<(HTMLSpanElement | null)[]>([]);

  useEffect(() => {
    let cancelled = false;
    let ctx: any;
    Promise.all([import("gsap"), import("gsap/ScrollTrigger")]).then(
      ([{ gsap }, { ScrollTrigger }]) => {
      if (cancelled) return;
      
        gsap.registerPlugin(ScrollTrigger);

        const tl = gsap.timeline({
          defaults: { ease: "power3.out" },
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 72%",
          },
        });

        tl
          .from(labelRef.current,    { opacity: 0, x: -24, duration: 0.6 })
          .from(titleRef.current,    { opacity: 0, y: 40,  duration: 0.75 }, "-=0.3")
          .from(subtitleRef.current, { opacity: 0, y: 24,  duration: 0.6  }, "-=0.45")
          .from(bodyRef.current,     { opacity: 0, y: 20,  duration: 0.6  }, "-=0.4")
          .from(linkRef.current,     { opacity: 0, x: -16, duration: 0.5  }, "-=0.35");

        gsap.from(
          statsGridRef.current ? Array.from(statsGridRef.current.children) : [],
          {
            opacity: 0,
            y: 60,
            duration: 0.7,
            stagger: 0.15,
            ease: "power3.out",
            scrollTrigger: {
              trigger: statsGridRef.current,
              start: "top 80%",
            },
          }
        );

        numberRefs.current.forEach((el) => {
          if (!el) return;
          gsap.from(el, {
            innerText: 0,
            duration: 2,
            snap: { innerText: 1 },
            ease: "power2.out",
            scrollTrigger: {
              trigger: el,
              start: "top 85%",
            },
          });
        });
      }
    );

    return () => { cancelled = true; ctx?.revert(); };
  }, []);

  return (
    <section ref={sectionRef} className="w-full bg-white p-6 md:p-12 lg:p-16 selection:bg-primary/20">
      <div className="max-w-8xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">

        <div className="lg:col-span-6 space-y-6 md:space-y-8">
          <span ref={labelRef} className="block text-base md:text-lg font-medium text-gray-600 tracking-wide">
            {t('about_us')}
          </span>

          <h2 ref={titleRef} className="text-3xl sm:text-4xl lg:text-4xl xl:text-6xl font-bold tracking-tight leading-[1.1] text-black">
            {t('title')}
          </h2>

          <span ref={subtitleRef} className="block text-base md:text-lg font-medium text-gray-600">
            {t('subtitle')}
          </span>

          <p ref={bodyRef} className="text-gray-500 text-sm sm:text-base leading-relaxed max-w-xl">
            {t('about1')}
          </p>

          <div ref={linkRef}>
            <Link
              href="/about"
              className="inline-flex items-center color-primary font-semibold text-base md:text-lg group hover:opacity-80 transition-opacity"
            >
              {t('read_more')}
              <span className="ml-2 transform group-hover:translate-x-1 transition-transform duration-200">
                &rarr;
              </span>
            </Link>
          </div>
        </div>

        <div ref={statsGridRef} className="lg:col-span-6 w-full grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 lg:pt-12">
          {stats.map((stat, i) => (
            <div
              key={stat.label}
              className="bg-[#efefef] p-6 md:p-8 flex flex-col justify-between aspect-square w-full max-w-[240px] md:max-w-none mx-auto"
            >
              <div>
                <span
                  ref={(el) => { numberRefs.current[i] = el; }}
                  className="block text-4xl sm:text-5xl lg:text-6xl font-bold color-primary tracking-tight"
                >
                  {stat.number}
                </span>
                <div className="w-10 h-[3px] bg-primary mt-2 mb-4" />
              </div>
              <div className="text-gray-800 font-medium text-base md:text-lg leading-tight">
                {t(stat.label)}
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
